'use client';

import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { FullscreenTransformStateContext } from '@/app/contexts';
import {
  appendCssScaleTransform,
  getFullscreenZoomCssScaleBoost,
  getFullscreenZoomScale,
  getFullscreenZoomVisualSize,
  preserveFullscreenScrollTopLeft,
  registerFullscreenZoomPreviewApplier,
  useDeferredFullscreenZoom,
} from '@/lib/fullscreen-zoom';

type FullscreenZoomableViewportProps = {
  isFullscreen: boolean;
  zoomLevel: number;
  /**
   * When true (fullscreen or Evidence Preview / source card), apply zoom viewport
   * behavior even if ``isFullscreen`` is false.
   */
  zoomActive?: boolean;
  className?: string;
  contentClassName?: string;
  /** Stretch to the fullscreen content area (scrollable viewers). Skips centering/scale. */
  fill?: boolean;
  children: React.ReactNode;
};

type FullscreenScrollCanvasProps = {
  containerWidth: number;
  containerHeight: number;
  contentWidth: number;
  contentHeight: number;
  className?: string;
  contentClassName?: string;
  children: React.ReactNode;
};

/** Scrollable canvas with explicit layout size so zoomed/rotated content is fully reachable.
 * Content is centered when it fits; top/left-anchored when it overflows (scroll 0,0). */
function FullscreenScrollCanvas({
  containerWidth,
  containerHeight,
  contentWidth,
  contentHeight,
  className = '',
  contentClassName = '',
  children,
}: FullscreenScrollCanvasProps) {
  const canvasWidth = Math.max(containerWidth, contentWidth);
  const canvasHeight = Math.max(containerHeight, contentHeight);
  const overflowsX = contentWidth > containerWidth;
  const overflowsY = contentHeight > containerHeight;

  return (
    <div
      className={`relative box-border min-h-0 min-w-0 ${className}`.trim()}
      style={{
        height: canvasHeight > 0 ? canvasHeight : '100%',
        minHeight: '100%',
        minWidth: '100%',
        width: canvasWidth > 0 ? canvasWidth : '100%',
      }}
    >
      <div
        className={`absolute box-border ${contentClassName}`.trim()}
        style={{
          height: contentHeight > 0 ? contentHeight : '100%',
          left: canvasWidth > 0 ? (overflowsX ? 0 : (canvasWidth - contentWidth) / 2) : 0,
          top: canvasHeight > 0 ? (overflowsY ? 0 : (canvasHeight - contentHeight) / 2) : 0,
          width: contentWidth > 0 ? contentWidth : '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Centers fullscreen content, preserves fit-to-viewport baseline, applies uniform zoom steps. */
export function FullscreenZoomableViewport({
  isFullscreen,
  zoomLevel: zoomLevelProp = 0,
  zoomActive: zoomActiveProp,
  className = '',
  contentClassName = '',
  fill = false,
  children,
}: FullscreenZoomableViewportProps) {
  const zoomActive = zoomActiveProp ?? isFullscreen;
  const viewportRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const { layoutScale, previewScale, cssScaleBoost } = useDeferredFullscreenZoom(isFullscreen, zoomLevelProp);
  const scaleInnerRef = useRef<HTMLDivElement>(null);
  const canvasSizeRef = useRef({ width: 0, height: 0 });
  const baselineSizeRef = useRef({ width: 0, height: 0 });
  const scale = isFullscreen ? layoutScale : getFullscreenZoomScale(zoomLevelProp);
  const previewTransformScale = isFullscreen ? previewScale : scale;

  useEffect(() => {
    if (!isFullscreen || fill) {
      return;
    }
    registerFullscreenZoomPreviewApplier((previewLevel, settledLevel) => {
      const el = scaleInnerRef.current;
      const scrollEl = viewportRef.current;
      if (!el) {
        return;
      }
      const nextScale = getFullscreenZoomScale(previewLevel);
      el.style.transform = `scale(${nextScale})`;
      el.style.willChange = previewLevel !== settledLevel ? 'transform' : '';
      if (scrollEl && baselineSizeRef.current.width > 0) {
        const nextW = Math.round(baselineSizeRef.current.width * nextScale);
        const nextH = Math.round(baselineSizeRef.current.height * nextScale);
        // FullscreenScrollCanvas sizes from contentWidth/Height on its outer
        // wrapper; update that wrapper (parent of the absolute-centered slot).
        const canvas = el.parentElement?.parentElement;
        if (canvas) {
          canvas.style.width = `${Math.max(scrollEl.clientWidth, nextW)}px`;
          canvas.style.height = `${Math.max(scrollEl.clientHeight, nextH)}px`;
        }
        preserveFullscreenScrollTopLeft(
          scrollEl,
          Math.max(scrollEl.clientWidth, nextW),
          Math.max(scrollEl.clientHeight, nextH),
        );
        canvasSizeRef.current = { width: nextW, height: nextH };
      }
    });
    return () => registerFullscreenZoomPreviewApplier(null);
  }, [fill, isFullscreen]);

  useEffect(() => {
    if (!zoomActive || fill) {
      return;
    }
    const el = viewportRef.current;
    if (!el) {
      return;
    }
    const syncSize = () => {
      setContainerSize({
        width: el.clientWidth,
        height: el.clientHeight,
      });
    };
    syncSize();
    const observer = new ResizeObserver(syncSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fill, zoomActive]);

  if (!zoomActive) {
    return <>{children}</>;
  }
  if (fill) {
    return (
      <div
        className={`box-border flex min-h-0 flex-1 w-full flex-col overflow-hidden ${className}`.trim()}
      >
        {children}
      </div>
    );
  }

  const { width: containerWidth, height: containerHeight } = containerSize;
  const baselineWidth = containerWidth > 0 ? containerWidth : 0;
  const baselineHeight = containerHeight > 0 ? containerHeight : 0;
  baselineSizeRef.current = { width: baselineWidth, height: baselineHeight };
  // Size the scroll canvas to the previewed visual scale (not settled layout) so
  // settle does not grow the canvas out from under a CSS-scaled preview.
  const visualScale = isFullscreen ? previewScale : scale;
  const contentWidth = baselineWidth > 0 ? Math.round(baselineWidth * visualScale) : 0;
  const contentHeight = baselineHeight > 0 ? Math.round(baselineHeight * visualScale) : 0;
  canvasSizeRef.current = { width: contentWidth, height: contentHeight };

  return (
    <div
      ref={viewportRef}
      className={`box-border min-h-0 flex-1 w-full overflow-auto custom-scrollbar2 ${className}`.trim()}
    >
      {baselineWidth > 0 && baselineHeight > 0 ? (
        <FullscreenScrollCanvas
          containerWidth={baselineWidth}
          containerHeight={baselineHeight}
          contentWidth={contentWidth}
          contentHeight={contentHeight}
          contentClassName={contentClassName}
        >
          <div
            ref={scaleInnerRef}
            className="box-border flex items-center justify-center"
            style={{
              height: baselineHeight,
              transform: `scale(${previewTransformScale})`,
              transformOrigin: 'center center',
              width: baselineWidth,
              willChange: cssScaleBoost !== 1 ? 'transform' : undefined,
            }}
          >
            {children}
          </div>
        </FullscreenScrollCanvas>
      ) : (
        <div className={`box-border flex h-full w-full items-center justify-center ${contentClassName}`.trim()}>
          {children}
        </div>
      )}
    </div>
  );
}

/** HTML / website iframes: fill the viewer and zoom via CSS ``zoom`` (not transform scale). */
export function IframeZoomViewport({
  zoomLevel,
  zoomActive = false,
  className = '',
  children,
}: {
  zoomLevel: number;
  zoomActive?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const scale = getFullscreenZoomScale(zoomLevel);
  return (
    <div
      className={`box-border flex min-h-0 min-w-0 flex-1 w-full flex-col overflow-auto custom-scrollbar2 ${className}`.trim()}
    >
      <div
        className="box-border w-full flex-1"
        style={{
          minHeight: 400,
          ...(zoomActive && scale !== 1 ? { zoom: scale } : {}),
        }}
      >
        {children}
      </div>
    </div>
  );
}

type FullscreenImageViewportProps = {
  persistedRotation?: number;
  /** Local temporary rotation when not reading fullscreen transform context. */
  temporaryRotation?: number;
  /** When false (Evidence Preview), uses local zoomLevel instead of fullscreen context. */
  isFullscreen?: boolean;
  zoomLevel?: number;
  /**
   * When set (SourceCard Evidence Preview), skip ResizeObserver measurement and use
   * this fixed scrollport. Avoids scrollbar ↔ contain-fit flicker that also breaks zoom.
   */
  lockedViewportWidth?: number;
  lockedViewportHeight?: number;
  src: string;
  alt: string;
  onLoad: () => void;
  onError: (event: React.SyntheticEvent<HTMLImageElement>) => void;
};

/**
 * Fullscreen / Evidence Preview image viewer with two rendering paths:
 *
 *   • Measured path (preferred): contain-fit the rotated natural size into the
 *     scrollport, then zoom. Canvas is ``max(container, visualBox)``. When the
 *     image fits, it is centered; when it overflows an axis, that axis is
 *     top/left-anchored so scroll ``(0,0)`` shows the start edge (same as PdfViewer).
 *
 *   • Fallback path (no measurement needed): a percentage-sized canvas
 *     (``zoomScale × 100%``, min 100%) with an ``object-contain`` image.
 */
export const FullscreenImageViewport = memo(function FullscreenImageViewport({
  persistedRotation = 0,
  temporaryRotation: temporaryRotationProp,
  isFullscreen = true,
  zoomLevel = 0,
  lockedViewportWidth,
  lockedViewportHeight,
  src,
  alt,
  onLoad,
  onError,
}: FullscreenImageViewportProps) {
  const { temporaryRotation: fullscreenTemporaryRotation } = useContext(FullscreenTransformStateContext);
  const { layoutScale, cssScaleBoost, previewScale } = useDeferredFullscreenZoom(isFullscreen, zoomLevel);
  const temporaryRotation = isFullscreen
    ? fullscreenTemporaryRotation
    : temporaryRotationProp;
  const rotationDeg = (((temporaryRotation ?? persistedRotation ?? 0) % 360) + 360) % 360;
  const rotated90 = rotationDeg === 90 || rotationDeg === 270;
  const hasLockedViewport =
    !!lockedViewportWidth &&
    lockedViewportWidth > 0 &&
    !!lockedViewportHeight &&
    lockedViewportHeight > 0;

  const scrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasSizeRef = useRef({ width: 0, height: 0 });
  const settledBoxRef = useRef({ width: 0, height: 0 });
  const baseImageTransformRef = useRef('');
  baseImageTransformRef.current = `translate(-50%, -50%) rotate(${rotationDeg}deg)`;

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }
    registerFullscreenZoomPreviewApplier((previewLevel, settledLevel) => {
      const img = imgRef.current;
      const canvas = canvasRef.current;
      const scrollEl = scrollRef.current;
      if (!img) {
        return;
      }
      const boost = getFullscreenZoomCssScaleBoost(previewLevel, settledLevel);
      img.style.transform = appendCssScaleTransform(baseImageTransformRef.current, boost);
      img.style.willChange = boost !== 1 ? 'transform' : '';
      if (canvas && settledBoxRef.current.width > 0) {
        const nextW = getFullscreenZoomVisualSize(settledBoxRef.current.width, boost);
        const nextH = getFullscreenZoomVisualSize(settledBoxRef.current.height, boost);
        const vpW = scrollEl?.clientWidth ?? 0;
        const vpH = scrollEl?.clientHeight ?? 0;
        const canvasW = Math.max(vpW, nextW);
        const canvasH = Math.max(vpH, nextH);
        const overflowsX = nextW > vpW;
        const overflowsY = nextH > vpH;
        canvas.style.width = `${canvasW}px`;
        canvas.style.height = `${canvasH}px`;
        // Pin image center to top-left of visual box when overflowing; else center in canvas.
        img.style.left = overflowsX ? `${nextW / 2}px` : '50%';
        img.style.top = overflowsY ? `${nextH / 2}px` : '50%';
        if (scrollEl) {
          preserveFullscreenScrollTopLeft(scrollEl, canvasW, canvasH);
        }
        canvasSizeRef.current = { width: nextW, height: nextH };
      }
    });
    return () => registerFullscreenZoomPreviewApplier(null);
  }, [isFullscreen, rotationDeg]);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    setNaturalSize({ width: 0, height: 0 });
  }, [src]);

  // Locked SourceCard viewport: trust parent AR box — never ResizeObserver (scrollbar loops).
  useLayoutEffect(() => {
    if (!hasLockedViewport) return;
    setContainerSize((prev) =>
      prev.width === lockedViewportWidth && prev.height === lockedViewportHeight
        ? prev
        : { width: lockedViewportWidth!, height: lockedViewportHeight! },
    );
  }, [hasLockedViewport, lockedViewportWidth, lockedViewportHeight]);

  // Fullscreen: measure the scroll element aggressively.
  useLayoutEffect(() => {
    if (hasLockedViewport) return;
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    let raf = 0;
    let tries = 0;
    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      setContainerSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
      if ((width === 0 || height === 0) && tries < 10) {
        tries += 1;
        raf = requestAnimationFrame(measure);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [hasLockedViewport]);

  const measured =
    containerSize.width > 0 &&
    containerSize.height > 0 &&
    naturalSize.width > 0 &&
    naturalSize.height > 0;

  // Contain the *rotated* image in the container, then apply zoom.
  const effectiveW = rotated90 ? naturalSize.height : naturalSize.width;
  const effectiveH = rotated90 ? naturalSize.width : naturalSize.height;
  const baseScale = measured
    ? Math.min(containerSize.width / effectiveW, containerSize.height / effectiveH)
    : 0;
  // Floor (not round) so the contain-fit box never exceeds the scrollport by 1px.
  const displayWidth = Math.max(1, Math.floor(naturalSize.width * baseScale * layoutScale));
  const displayHeight = Math.max(1, Math.floor(naturalSize.height * baseScale * layoutScale));
  const boxWidth = rotated90 ? displayHeight : displayWidth;
  const boxHeight = rotated90 ? displayWidth : displayHeight;
  settledBoxRef.current = { width: boxWidth, height: boxHeight };
  const visualBoxWidth = getFullscreenZoomVisualSize(boxWidth, cssScaleBoost);
  const visualBoxHeight = getFullscreenZoomVisualSize(boxHeight, cssScaleBoost);
  const canvasWidth = Math.max(containerSize.width, visualBoxWidth);
  const canvasHeight = Math.max(containerSize.height, visualBoxHeight);
  const overflowsX = measured && visualBoxWidth > containerSize.width;
  const overflowsY = measured && visualBoxHeight > containerSize.height;
  // Absolute center of the img (with translate(-50%,-50%)): top-left when overflowing.
  const imageCenterLeft = overflowsX ? visualBoxWidth / 2 : canvasWidth / 2;
  const imageCenterTop = overflowsY ? visualBoxHeight / 2 : canvasHeight / 2;

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.width = '';
      canvas.style.height = '';
    }
    if (!scrollEl || !measured) {
      canvasSizeRef.current = { width: visualBoxWidth, height: visualBoxHeight };
      return;
    }
    preserveFullscreenScrollTopLeft(scrollEl, canvasWidth, canvasHeight);
    canvasSizeRef.current = { width: visualBoxWidth, height: visualBoxHeight };
  }, [canvasWidth, canvasHeight, measured, visualBoxWidth, visualBoxHeight]);

  const zoomPercent = Math.max(100, Math.round(previewScale * 100));
  const imageTransform = appendCssScaleTransform(
    `translate(-50%, -50%) rotate(${rotationDeg}deg)`,
    cssScaleBoost,
  );

  return (
    <div
      ref={scrollRef}
      className="mediaDisplayImageDiv box-border min-h-0 flex-1 w-full overflow-auto custom-scrollbar2"
      style={
        hasLockedViewport
          ? {
              width: '100%',
              height: lockedViewportHeight,
              minHeight: lockedViewportHeight,
              maxHeight: lockedViewportHeight,
            }
          : undefined
      }
    >
      {measured ? (
        <div
          ref={canvasRef}
          className="relative box-border"
          style={{
            height: canvasHeight,
            minHeight: hasLockedViewport ? undefined : '100%',
            minWidth: hasLockedViewport ? undefined : '100%',
            width: canvasWidth,
          }}
        >
          <img
            ref={imgRef}
            aria-label="Image"
            src={src}
            alt={alt}
            className="mediaDisplayImageContent box-border max-h-none max-w-none rounded-sm select-none"
            style={{
              height: displayHeight,
              left: imageCenterLeft,
              position: 'absolute',
              top: imageCenterTop,
              transform: imageTransform,
              transformOrigin: 'center center',
              width: displayWidth,
              willChange: cssScaleBoost !== 1 ? 'transform' : undefined,
            }}
            onLoad={(event) => {
              const img = event.currentTarget;
              setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
              onLoad();
            }}
            onError={onError}
          />
        </div>
      ) : (
        <div
          className="box-border flex items-center justify-center"
          style={{
            height: hasLockedViewport ? '100%' : `${zoomPercent}%`,
            minHeight: hasLockedViewport ? undefined : '100%',
            minWidth: hasLockedViewport ? undefined : '100%',
            width: hasLockedViewport ? '100%' : `${zoomPercent}%`,
          }}
        >
          <img
            ref={imgRef}
            aria-label="Image"
            src={src}
            alt={alt}
            className="mediaDisplayImageContent box-border h-full w-full rounded-sm object-contain select-none"
            style={{
              transform: appendCssScaleTransform(`rotate(${rotationDeg}deg)`, cssScaleBoost),
              transformOrigin: 'center center',
              willChange: cssScaleBoost !== 1 ? 'transform' : undefined,
            }}
            onLoad={(event) => {
              const img = event.currentTarget;
              setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
              if (!hasLockedViewport) {
                const el = scrollRef.current;
                if (el) {
                  setContainerSize({ width: el.clientWidth, height: el.clientHeight });
                }
              }
              onLoad();
            }}
            onError={onError}
          />
        </div>
      )}
    </div>
  );
});
